import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api, type QuestionItem, type QuestionsForGroup, type ResultStatus } from '../api.js';
import Avatar from '../components/Avatar.js';
import DateCalendar from '../components/DateCalendar.js';
import {
  formatCalendarDay,
  formatDeltaMinutes,
  formatGuessAtNoon,
  formatKickoffParts,
  formatWallClock,
} from '../lib/intl.js';
import matchStyles from './PredictionsScreen.module.css';
import styles from './CustomQuestionsSection.module.css';

/**
 * A custom competition's question "rectangle" is deliberately built from the
 * exact same classes a real football match's `MatchCard`
 * (`PredictionsScreen.tsx`) uses — imported here as `matchStyles` — rather
 * than a lookalike copy: the topbar (a pill + status chip + deadline, where
 * a real match shows a stage pill + status chip + kickoff time), the
 * lock/reveal machinery, and the "View predictions" toggle are all styled
 * and behave identically. Only what's genuinely different per question kind
 * (the options row, the football scoreline inputs) uses this file's own
 * `styles`. The two were visibly diverging before this file mirrored
 * `MatchCard` directly — this is what keeps them from drifting apart again.
 */

/** The three states a real match's status chip has, approximated from a question's own fields (no server-side "reason" field for custom questions). */
function statusChip(
  question: QuestionItem,
  t: ReturnType<typeof useTranslation>['t'],
): { label: string; className: string | undefined } | null {
  if (question.revealedAt) {
    return { label: t('predictions.status.finished'), className: matchStyles.statusFinished };
  }
  if (!question.editable && question.myLockedAt) {
    return { label: t('predictions.status.locked'), className: matchStyles.statusLocked };
  }
  if (!question.editable) {
    return { label: t('predictions.status.inProgress'), className: matchStyles.statusInProgress };
  }
  return null;
}

/** The same two-line date/time block `MatchCard` shows for a real kickoff — here, the question's deadline. */
function deadlineBlock(deadlineAt: string | null, locale: string) {
  const parts = formatKickoffParts(deadlineAt, locale);
  if (!parts) return null;
  return (
    <div className={matchStyles.kickoffBlock}>
      <span className={matchStyles.kickoffDate}>{parts.date}</span>
      <span className={matchStyles.kickoffTime}>{parts.time}</span>
    </div>
  );
}

/** Mirrors `MatchCard`'s bottom "why can't I edit this" line — shown for every closed reason except "it's over", same as the real card. */
function lockReasonLine(question: QuestionItem, t: ReturnType<typeof useTranslation>['t']) {
  if (question.editable || question.revealedAt) return null;
  const key = question.myLockedAt ? 'lockedByParticipant' : 'deadlinePassed';
  return <p className={matchStyles.lockReason}>{t(`customCompetition.lockReason.${key}`)}</p>;
}

/**
 * The organizer-only actions shared by both question kinds' topbars: "Enter
 * results" (or "Edit results", once revealed) is exclusively for entering
 * the real-world outcome and the points it earns — never anything else, so
 * an organizer opening it mid-matchday to type a score never lands on a
 * settings form by accident. Everything that ISN'T the result — deadline,
 * lock permission, display/results format, per-answer styling, the football
 * scoring parameters — lives behind the separate "Edit question" button
 * instead, both routing to the same `EditQuestionResultsScreen` with a
 * different `mode`. Its own component (not just shared JSX) because the
 * remove mutation needs its own `useMutation` instance.
 */
function OrganizerActions({
  question,
  groupId,
  poolId,
}: {
  question: QuestionItem;
  groupId: string;
  poolId: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const remove = useMutation({
    mutationFn: () => api.deleteQuestion(groupId, poolId, question.id),
    onSuccess: async () => {
      // Removing a question can change the leaderboard total (its
      // question_scores rows are gone too), not just the question list.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['questions', groupId, poolId] }),
        queryClient.invalidateQueries({ queryKey: ['custom-leaderboard', groupId, poolId] }),
      ]);
    },
  });

  return (
    <div className={styles.organizerActions}>
      <Link
        to={`/groups/${groupId}/bolaos/${poolId}/questions/${question.id}/results`}
        className={styles.editLink}
      >
        {question.revealedAt
          ? t('customCompetition.editResults')
          : t('customCompetition.enterResults')}
      </Link>
      <Link
        to={`/groups/${groupId}/bolaos/${poolId}/questions/${question.id}/edit`}
        className={styles.editLink}
      >
        {t('customCompetition.editQuestion')}
      </Link>
      <button
        type="button"
        className={styles.removeQuestion}
        aria-label={t('customCompetition.removeQuestion')}
        disabled={remove.isPending}
        onClick={() => {
          if (window.confirm(t('customCompetition.removeQuestionConfirm'))) remove.mutate();
        }}
      >
        ×
      </button>
    </div>
  );
}

/**
 * "See points details" — collapsed by default, visible to everyone (not
 * just the organizer), on every question card. Shows how the question is
 * scored: a `multiple_choice` question's point range (or lack of one, for
 * `winner_loser`), or a `football` question's `MatchParams` — the exact
 * formula `computeMatchPoints` (`@bolao/scoring`) scores it with, same one
 * a real football match uses.
 */
function PointsDetails({ question }: { question: QuestionItem }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className={styles.detailsWrapper}>
      <button type="button" className={styles.detailsToggle} onClick={() => setOpen((o) => !o)}>
        {open ? t('customCompetition.pointsDetailsHide') : t('customCompetition.pointsDetailsShow')}
      </button>

      {open ? (
        <div className={styles.detailsPanel}>
          {question.kind === 'football' ? (
            <>
              <p className={styles.detailsRow}>
                {t('customCompetition.pointsDetailsFootballIntro')}
              </p>
              <p className={styles.detailsRow}>
                {t('customCompetition.matchOutcomeLabel')}: {question.matchOutcome}
              </p>
              <p className={styles.detailsRow}>
                {t('customCompetition.matchExactLabel')}: {question.matchExact}
              </p>
              <p className={styles.detailsRow}>
                {t('customCompetition.matchPotMultiplierLabel')}: {question.matchPotMultiplier}
              </p>
              <p className={styles.detailsRow}>
                {t('customCompetition.matchDecayLabel')}: {question.matchDecay}
              </p>
              <p className={styles.detailsRow}>
                {t('customCompetition.matchThresholdLabel')}: {question.matchThreshold}
              </p>
              {question.canGoToPenalties ? (
                <>
                  <p className={styles.detailsRow}>
                    {t('customCompetition.matchPenaltyExactLabel')}: {question.matchPenaltyExact}
                  </p>
                  <p className={styles.detailsRow}>
                    {t('customCompetition.matchPenaltyWrongLabel')}: {question.matchPenaltyWrong}
                  </p>
                </>
              ) : null}
            </>
          ) : question.kind === 'date' ? (
            <>
              {/* Rule 2 is the one people are most likely to be surprised by,
                  so it leads regardless of mode: a date vote counts as noon. */}
              <p className={styles.detailsRow}>{t('customCompetition.pointsDetailsDateIntro')}</p>
              <p className={styles.detailsRow}>
                {question.dateScoringMode === 'placement' || question.dateScoringMode === null
                  ? t('customCompetition.dateScoringPlacementHelp')
                  : t(
                      `customCompetition.dateScoring${question.dateScoringMode === 'ladder' ? 'Ladder' : question.dateScoringMode === 'closeness' ? 'Closeness' : 'WinnerTakesAll'}Detail`,
                      {
                        min: question.minPoints ?? 0,
                        max: question.maxPoints ?? 0,
                      },
                    )}
              </p>
            </>
          ) : question.scoringMode === 'per_answer' ? (
            <p className={styles.detailsRow}>
              {question.minPoints !== null && question.maxPoints !== null
                ? t('customCompetition.pointsDetailsPerAnswerRange', {
                    min: question.minPoints,
                    max: question.maxPoints,
                  })
                : t('customCompetition.pointsDetailsPerAnswerNoRange')}
            </p>
          ) : (
            <p className={styles.detailsRow}>{t('customCompetition.pointsDetailsWinnerLoser')}</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The "View predictions" toggle + revealed list — the exact interaction
 * `MatchCard` uses (a real match's `revealButton`/`revealHint`/`revealed`),
 * just fed a question's own `revealed` picks instead of a match's. `render`
 * turns one revealed pick into its row's trailing content (an option label
 * for `multiple_choice`, a scoreline for `football`) — the only thing that
 * differs between the two kinds here. `points`, when given, adds a per-person
 * points-earned badge next to it — only meaningful for a `per_answer`
 * `multiple_choice` question, where each option already carries its own
 * `pointsAwarded` once revealed.
 */
function RevealSection({
  question,
  render,
  points,
  standings = false,
}: {
  question: QuestionItem;
  render: (pick: QuestionItem['revealed'][number]) => string;
  points?: (pick: QuestionItem['revealed'][number]) => number | null;
  /** Render the ranked standings table instead of the flat list — `leaderboard` results format. */
  standings?: boolean;
}) {
  const { t } = useTranslation();
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealHint, setRevealHint] = useState(false);
  const canReveal = question.canRevealOthers;

  return (
    <>
      <div className={matchStyles.revealRow}>
        <button
          type="button"
          className={`${matchStyles.revealButton} ${!canReveal ? matchStyles.revealButtonDisabled : ''}`}
          aria-disabled={!canReveal}
          title={canReveal ? undefined : t('customCompetition.revealHint')}
          onClick={() => {
            if (!canReveal) {
              setRevealHint(true);
              return;
            }
            setRevealHint(false);
            setRevealOpen((o) => !o);
          }}
        >
          {revealOpen && canReveal
            ? t('predictions.hidePredictions')
            : t('predictions.viewPredictions')}
        </button>
        {revealHint && !canReveal ? (
          <p className={matchStyles.revealHint}>{t('customCompetition.revealHint')}</p>
        ) : null}
      </div>

      {revealOpen && canReveal ? (
        <div className={matchStyles.revealed}>
          {question.revealed.length === 0 ? (
            <p className={matchStyles.revealedEmpty}>{t('customCompetition.revealedEmpty')}</p>
          ) : standings ? (
            <DateStandingsTable question={question} />
          ) : (
            <ul className={matchStyles.revealedList}>
              {question.revealed.map((pick) => {
                const pointsAwarded = points?.(pick) ?? null;
                return (
                  <li key={pick.participantId} className={matchStyles.revealedRow}>
                    <Avatar name={pick.displayName} imageUrl={pick.image} size={36} />
                    <span className={matchStyles.revealedName}>{pick.displayName}</span>
                    {pointsAwarded !== null ? (
                      <span className={styles.pointsBadge}>
                        {t('customCompetition.pointsBadge', { count: pointsAwarded })}
                      </span>
                    ) : null}
                    <span className={matchStyles.revealedScore}>{render(pick)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </>
  );
}

/** 1st/2nd/3rd get a medal; everyone else just their number. */
const MEDALS = ['🥇', '🥈', '🥉'];

/**
 * The `leaderboard` results format for a `date` question (PLAN.md Phase
 * 10.16): who guessed closest, in order, with each row's chosen day, how far
 * off it was, and — in a points mode — what that placing earned.
 *
 * Every figure here comes straight off `question.revealed`, already ranked and
 * priced server-side by `date-scoring.ts`. Nothing is recomputed locally: the
 * ordering shown has to be the same one written into `question_scores` at
 * reveal, and a second implementation in the browser is exactly how those two
 * would drift apart. Sorting by the server's own `rank` is the one liberty
 * taken, since the API returns picks in no particular order.
 *
 * Tied guesses share a rank, so the medal follows the rank rather than the row
 * position — two people can genuinely both hold 🥇.
 */
function DateStandingsTable({ question }: { question: QuestionItem }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';

  // A pick with no standing hasn't been ranked (no real result yet, or an
  // unparseable guess) — it sits after everyone who has one rather than
  // claiming a place.
  const ranked = [...question.revealed].sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));
  const showPoints = question.dateScoringMode !== null && question.dateScoringMode !== 'placement';

  return (
    <div className={styles.standings}>
      <div className={`${styles.standingsRow} ${styles.standingsHead}`}>
        <span className={styles.standingsRank}>{t('customCompetition.standingsRank')}</span>
        <span className={styles.standingsName}>{t('customCompetition.standingsParticipant')}</span>
        <span className={styles.standingsGuess}>{t('customCompetition.standingsChosenDate')}</span>
        <span className={styles.standingsDelta}>{t('customCompetition.standingsDelta')}</span>
        {showPoints ? (
          <span className={styles.standingsPoints}>{t('customCompetition.standingsPoints')}</span>
        ) : null}
      </div>

      {ranked.map((pick) => {
        const medal = pick.rank !== null ? MEDALS[pick.rank - 1] : undefined;
        return (
          <div
            key={pick.participantId}
            className={`${styles.standingsRow} ${pick.rank !== null && pick.rank <= 3 ? styles[`standingsPodium${pick.rank}`] : ''}`}
          >
            <span className={styles.standingsRank}>
              {pick.rank !== null ? (
                <>
                  {t('customCompetition.standingsPlace', { count: pick.rank, ordinal: true })}
                  {medal ? <span className={styles.standingsMedal}>{medal}</span> : null}
                </>
              ) : (
                '—'
              )}
            </span>
            <span className={styles.standingsName}>{pick.displayName}</span>
            {/* With the noon it counts as, since that — not the bare day — is
                what the gap in the next column is measured from. */}
            <span className={styles.standingsGuess}>
              {formatGuessAtNoon(pick.dateGuess, locale) ?? '—'}
            </span>
            <span className={styles.standingsDelta}>
              {pick.deltaMinutes !== null ? formatDeltaMinutes(pick.deltaMinutes) : '—'}
            </span>
            {showPoints ? (
              <span className={styles.standingsPoints}>
                {pick.points !== null
                  ? t('customCompetition.pointsBadge', { count: pick.points })
                  : '—'}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * What one answer's result badge should say, or null for "nothing decided
 * yet". A `per_answer` question carries its own three-way status; a
 * `winner_loser` one only ever distinguishes the winner, and only once the
 * question is revealed. Shared by both answer layouts so they can never
 * disagree about what a revealed answer looks like.
 */
function resultBadgeFor(
  question: QuestionItem,
  option: QuestionItem['options'][number],
): ResultStatus | null {
  if (question.scoringMode === 'per_answer') return option.resultStatus;
  return option.isWinner && question.revealedAt ? 'correct' : null;
}

/**
 * The `panels` results format (PLAN.md Phase 10.12): once revealed, each
 * answer becomes a full-height coloured panel showing how many people chose
 * it in large type, a crown over whichever turned out correct, and the names
 * of everyone who picked it.
 *
 * Replaces both the answer rows/cards and the collapsible reveal list for
 * that question — after a reveal there is nothing left to tap and nothing
 * left to earn by locking, so the picking affordance is just noise. It is
 * only ever rendered once `revealedAt` is set (`QuestionCard` gates it), so
 * `question.revealed` is guaranteed populated: `canRevealOthers` is
 * unconditionally true for a finished question.
 *
 * Every number here is derived from the same `question.revealed` the flat
 * list reads, rather than from a count the server sends separately — one
 * source, so the panels and the list can never disagree.
 *
 * The crown follows *correctness*, not points: a `winner_loser` question has
 * no points at all, and a `per_answer` one can mark a correct answer worth
 * zero. `resultBadgeFor` already encodes that distinction, so this reuses it
 * instead of re-deriving it.
 */
function AnswerPanels({ question }: { question: QuestionItem }) {
  const { t } = useTranslation();

  const picksByOption = new Map<string, QuestionItem['revealed']>();
  for (const pick of question.revealed) {
    if (!pick.optionId) continue;
    const list = picksByOption.get(pick.optionId) ?? [];
    list.push(pick);
    picksByOption.set(pick.optionId, list);
  }

  return (
    <div className={styles.panels}>
      {question.options.map((option) => {
        const picks = picksByOption.get(option.id) ?? [];
        const isCorrect = resultBadgeFor(question, option) === 'correct';
        const tint = option.color
          ? ({ '--answer-color': option.color } as React.CSSProperties)
          : undefined;

        return (
          <div
            key={option.id}
            className={`${styles.panel} ${isCorrect ? styles.panelCorrect : ''}`}
            style={tint}
          >
            {/* Sits above the label rather than beside it, so a long answer
                still wraps under a crown that stays centred on it. */}
            <span className={styles.panelCrown} aria-hidden={!isCorrect}>
              {isCorrect ? '👑' : null}
            </span>

            <span className={styles.panelLabel}>{option.label}</span>

            <div className={styles.panelFigure}>
              {option.imageUrl ? (
                <img src={option.imageUrl} alt="" className={styles.panelImage} />
              ) : null}
              {option.emoji && !option.imageUrl ? (
                <span className={styles.panelEmoji}>{option.emoji}</span>
              ) : null}
              <span className={styles.panelCount}>{picks.length}</span>
            </div>

            {question.scoringMode === 'per_answer' && option.pointsAwarded !== null ? (
              <span className={styles.pointsBadge}>
                {t('customCompetition.pointsBadge', { count: option.pointsAwarded })}
              </span>
            ) : null}

            {picks.length > 0 ? (
              <ul className={styles.panelNames}>
                {picks.map((pick) => (
                  <li key={pick.participantId} className={styles.panelName}>
                    {pick.displayName}
                  </li>
                ))}
              </ul>
            ) : (
              <span className={styles.panelNobody}>{t('customCompetition.panelsNobody')}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The `card` display format (PLAN.md Phase 10.11): each answer is a big
 * tappable square carrying its own colour, emoji and/or image, instead of a
 * full-width labelled row. Purely a different rendering of the same
 * `question.options` — same pick mutation, same disabled rules, same result
 * badges — so nothing about answering, locking or scoring changes with it.
 *
 * The organizer's colour is applied inline rather than through a class,
 * since it is per-answer data rather than one of a fixed set of themes. It
 * reaches this point having been validated as `#rrggbb` both client-side
 * (`customQuestionDraft.ts`) and by the API (`routes/custom.ts`).
 */
function AnswerCards({
  question,
  onPick,
  disabled,
}: {
  question: QuestionItem;
  onPick: (optionId: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className={styles.optionCards}>
      {question.options.map((option) => {
        const selected = question.myOptionId === option.id;
        const badge = resultBadgeFor(question, option);
        const tint = option.color
          ? ({ '--answer-color': option.color } as React.CSSProperties)
          : undefined;

        return (
          <button
            key={option.id}
            type="button"
            className={`${styles.optionCard} ${selected ? styles.optionCardSelected : ''} ${badge ? styles[`optionCard_${badge}`] : ''}`}
            style={tint}
            disabled={disabled}
            aria-pressed={selected}
            onClick={() => onPick(option.id)}
          >
            <span className={styles.cardCheck} aria-hidden="true" />
            {option.imageUrl ? (
              <img src={option.imageUrl} alt="" className={styles.cardImage} />
            ) : null}
            {option.emoji ? (
              <span className={option.imageUrl ? styles.cardEmojiSmall : styles.cardEmoji}>
                {option.emoji}
              </span>
            ) : null}
            <span className={styles.cardLabel}>{option.label}</span>
            {badge || (question.scoringMode === 'per_answer' && option.pointsAwarded !== null) ? (
              <span className={styles.cardBadges}>
                {badge ? (
                  <span className={`${styles.resultBadge} ${styles[`resultBadge_${badge}`]}`}>
                    {t(`customCompetition.result.${badge}`)}
                  </span>
                ) : null}
                {question.scoringMode === 'per_answer' && option.pointsAwarded !== null ? (
                  <span className={styles.pointsBadge}>
                    {t('customCompetition.pointsBadge', { count: option.pointsAwarded })}
                  </span>
                ) : null}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/** The original one-answer-per-row layout, and still the default for every question that doesn't ask for cards. */
function AnswerList({
  question,
  onPick,
  disabled,
}: {
  question: QuestionItem;
  onPick: (optionId: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div className={styles.options}>
      {question.options.map((option) => {
        const selected = question.myOptionId === option.id;
        const badge = resultBadgeFor(question, option);
        return (
          <button
            key={option.id}
            type="button"
            className={`${styles.optionButton} ${selected ? styles.optionButtonSelected : ''} ${badge ? styles[`optionButton_${badge}`] : ''}`}
            disabled={disabled}
            onClick={() => onPick(option.id)}
          >
            <span className={styles.optionLabel}>{option.label}</span>
            {badge || (question.scoringMode === 'per_answer' && option.pointsAwarded !== null) ? (
              <span className={styles.optionBadges}>
                {badge ? (
                  <span className={`${styles.resultBadge} ${styles[`resultBadge_${badge}`]}`}>
                    {t(`customCompetition.result.${badge}`)}
                  </span>
                ) : null}
                {question.scoringMode === 'per_answer' && option.pointsAwarded !== null ? (
                  <span className={styles.pointsBadge}>
                    {t('customCompetition.pointsBadge', { count: option.pointsAwarded })}
                  </span>
                ) : null}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * One `multiple_choice` question's "rectangle" — picking an answer instead
 * of typing a scoreline, and — since this is NOT a quiz — with no correct
 * answer to show until the organizer reveals one.
 */
function MultipleChoiceQuestionCard({
  question,
  groupId,
  poolId,
  locale,
  isOrganizer,
}: {
  question: QuestionItem;
  groupId: string;
  poolId: string;
  locale: string;
  isOrganizer: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const pick = useMutation({
    mutationFn: (optionId: string) =>
      api.saveQuestionPrediction(groupId, poolId, question.id, {
        kind: 'multiple_choice',
        optionId,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questions', groupId, poolId] }),
  });
  const lock = useMutation({
    mutationFn: () => api.lockQuestionPrediction(groupId, poolId, question.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questions', groupId, poolId] }),
  });

  const canLock =
    question.allowLockForever &&
    question.editable &&
    question.myOptionId !== null &&
    !question.myLockedAt;
  const answersDisabled = !question.editable || pick.isPending;
  const chip = statusChip(question, t);
  // The panels layout *is* the reveal, so it replaces both the picker and the
  // collapsible reveal list — and only once there's a result to show.
  const showPanels = question.resultsFormat === 'panels' && question.revealedAt !== null;

  return (
    <div
      className={`${matchStyles.matchCard} ${!question.editable ? matchStyles.matchCardLocked : ''}`}
    >
      <div className={matchStyles.matchCardTopbar}>
        <div className={matchStyles.topbarLeft}>
          <span className={matchStyles.stagePill}>
            {t('customCompetition.questionPill', { number: question.questionNumber })}
          </span>
          {chip ? (
            <span className={`${matchStyles.statusChip} ${chip.className}`}>{chip.label}</span>
          ) : null}
        </div>
        <div className={styles.topbarRight}>
          {deadlineBlock(question.deadlineAt, locale)}
          {isOrganizer ? (
            <OrganizerActions question={question} groupId={groupId} poolId={poolId} />
          ) : null}
        </div>
      </div>

      <div className={matchStyles.matchCardBody}>
        <p className={styles.prompt}>{question.prompt}</p>

        {showPanels ? (
          <AnswerPanels question={question} />
        ) : question.displayFormat === 'card' ? (
          <AnswerCards
            question={question}
            onPick={(id) => pick.mutate(id)}
            disabled={answersDisabled}
          />
        ) : (
          <AnswerList
            question={question}
            onPick={(id) => pick.mutate(id)}
            disabled={answersDisabled}
          />
        )}

        {question.myOptionId === null && !showPanels ? (
          <p className={styles.unanswered}>{t('customCompetition.unanswered')}</p>
        ) : null}

        <PointsDetails question={question} />

        {canLock ? (
          <div className={matchStyles.lockRow}>
            <button
              type="button"
              className={matchStyles.lockButton}
              disabled={lock.isPending}
              onClick={() => {
                if (window.confirm(t('customCompetition.lockConfirm'))) lock.mutate();
              }}
            >
              {lock.isPending ? t('predictions.locking') : t('predictions.lockForever')}
            </button>
          </div>
        ) : null}

        {showPanels ? null : (
          <RevealSection
            question={question}
            render={(p) => {
              // The emoji, when there is one, is how a card answer is
              // recognised at a glance — so the reveal list shows the same
              // thing the card does rather than the bare label.
              const option = question.options.find((o) => o.id === p.optionId);
              if (!option) return '—';
              return option.emoji ? `${option.emoji} ${option.label}` : option.label;
            }}
            points={(p) => {
              // `winner_loser` questions have no per-answer point value.
              if (question.scoringMode !== 'per_answer') return null;
              const option = question.options.find((o) => o.id === p.optionId);
              return option?.pointsAwarded ?? null;
            }}
          />
        )}
      </div>

      {lockReasonLine(question, t)}
    </div>
  );
}

/**
 * One `date` question's "rectangle" (PLAN.md Phase 10.16): the prompt, a month
 * calendar to tap a day on, and — once revealed — the real moment plus the
 * ranked standings behind "View predictions".
 *
 * Shares every wrapper class with the other two kinds (`matchStyles`), so a
 * mixed list of questions reads as one list of cards. What is genuinely
 * different is only the middle: a calendar instead of answer buttons or score
 * boxes.
 */
function DateQuestionCard({
  question,
  groupId,
  poolId,
  locale,
  isOrganizer,
}: {
  question: QuestionItem;
  groupId: string;
  poolId: string;
  locale: string;
  isOrganizer: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const pick = useMutation({
    mutationFn: (dateGuess: string) =>
      api.saveQuestionPrediction(groupId, poolId, question.id, { kind: 'date', dateGuess }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questions', groupId, poolId] }),
  });
  const lock = useMutation({
    mutationFn: () => api.lockQuestionPrediction(groupId, poolId, question.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questions', groupId, poolId] }),
  });

  const canLock =
    question.allowLockForever &&
    question.editable &&
    question.myDateGuess !== null &&
    !question.myLockedAt;
  const chip = statusChip(question, t);
  // The standings need something to rank against, so they only replace the flat
  // list once a real moment has been entered — see the `questionResultsFormat`
  // enum's note about a format degrading rather than erroring.
  const showStandings = question.resultsFormat === 'leaderboard' && question.realDateAt !== null;

  return (
    <div
      className={`${matchStyles.matchCard} ${!question.editable ? matchStyles.matchCardLocked : ''}`}
    >
      <div className={matchStyles.matchCardTopbar}>
        <div className={matchStyles.topbarLeft}>
          <span className={matchStyles.stagePill}>
            {t('customCompetition.questionPill', { number: question.questionNumber })}
          </span>
          {chip ? (
            <span className={`${matchStyles.statusChip} ${chip.className}`}>{chip.label}</span>
          ) : null}
        </div>
        <div className={styles.topbarRight}>
          {deadlineBlock(question.deadlineAt, locale)}
          {isOrganizer ? (
            <OrganizerActions question={question} groupId={groupId} poolId={poolId} />
          ) : null}
        </div>
      </div>

      <div className={matchStyles.matchCardBody}>
        <p className={styles.prompt}>{question.prompt}</p>

        <DateCalendar
          value={question.myDateGuess}
          targetMonth={question.dateTargetMonth}
          disabled={!question.editable || pick.isPending}
          onPick={(day) => pick.mutate(day)}
        />

        {question.myDateGuess === null ? (
          <p className={styles.unanswered}>{t('customCompetition.unanswered')}</p>
        ) : (
          // The day is the whole answer — nobody is asked for a time — but the
          // noon it counts as is what the ranking is measured from, so it is
          // shown rather than left to be discovered from the results.
          <p className={styles.guessReadout}>
            {t('customCompetition.dateYourGuess', {
              guess: formatGuessAtNoon(question.myDateGuess, locale, 'long') ?? '',
            })}
          </p>
        )}

        {question.realDateAt ? (
          <div className={matchStyles.realScoreBanner}>
            <span className={matchStyles.realScoreLabel}>
              {t('customCompetition.dateRealLabel')}
            </span>
            <span className={matchStyles.realScoreValue}>
              {formatWallClock(question.realDateAt, locale)}
            </span>
          </div>
        ) : null}

        <PointsDetails question={question} />

        {canLock ? (
          <div className={matchStyles.lockRow}>
            <button
              type="button"
              className={matchStyles.lockButton}
              disabled={lock.isPending}
              onClick={() => {
                if (window.confirm(t('customCompetition.lockConfirm'))) lock.mutate();
              }}
            >
              {lock.isPending ? t('predictions.locking') : t('predictions.lockForever')}
            </button>
          </div>
        ) : null}

        <RevealSection
          question={question}
          standings={showStandings}
          render={(p) => formatCalendarDay(p.dateGuess, locale) ?? '—'}
          points={(p) => (question.dateScoringMode === 'placement' ? null : p.points)}
        />
      </div>

      {lockReasonLine(question, t)}
    </div>
  );
}

/** What's being typed for one `football` question, before (or instead of) a save. */
interface FootballDraft {
  home: string;
  away: string;
  penaltyWinner: 'home' | 'away' | null;
}

function footballDraftFromQuestion(question: QuestionItem): FootballDraft {
  return {
    home: question.myHomeScore !== null ? String(question.myHomeScore) : '',
    away: question.myAwayScore !== null ? String(question.myAwayScore) : '',
    penaltyWinner: question.myPenaltyWinner,
  };
}

/**
 * One `football` question's "rectangle" — a scoreline prediction against a
 * real or organizer-typed match, sharing every class `MatchCard` uses for a
 * real match's own teams/score-inputs/reveal (`matchStyles`), just scoped
 * to one custom-competition question instead of a `matches` row.
 */
function FootballQuestionCard({
  question,
  groupId,
  poolId,
  locale,
  isOrganizer,
}: {
  question: QuestionItem;
  groupId: string;
  poolId: string;
  locale: string;
  isOrganizer: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<FootballDraft>(() => footballDraftFromQuestion(question));
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    setDraft(footballDraftFromQuestion(question));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- narrow: only resync when the server value itself changed.
  }, [question.myHomeScore, question.myAwayScore, question.myPenaltyWinner]);

  const lock = useMutation({
    mutationFn: () => api.lockQuestionPrediction(groupId, poolId, question.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['questions', groupId, poolId] }),
  });

  const homeNum = Number.parseInt(draft.home, 10);
  const awayNum = Number.parseInt(draft.away, 10);
  const bothEntered =
    draft.home !== '' && draft.away !== '' && !Number.isNaN(homeNum) && !Number.isNaN(awayNum);
  const isLevel = bothEntered && homeNum === awayNum;
  const needsPenaltyPick = isLevel && question.canGoToPenalties;

  async function commit(penaltyWinner: 'home' | 'away' | null) {
    if (!bothEntered) return;
    if (needsPenaltyPick && !penaltyWinner) return;

    setSaveState('saving');
    try {
      await api.saveQuestionPrediction(groupId, poolId, question.id, {
        kind: 'football',
        homeScore: homeNum,
        awayScore: awayNum,
        ...(penaltyWinner ? { penaltyWinner } : {}),
      });
      await queryClient.invalidateQueries({ queryKey: ['questions', groupId, poolId] });
      setSaveState('saved');
      setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch {
      setSaveState('error');
    }
  }

  const canLock =
    question.allowLockForever &&
    question.editable &&
    question.myHomeScore !== null &&
    !question.myLockedAt;
  const chip = statusChip(question, t);

  return (
    <div
      className={`${matchStyles.matchCard} ${!question.editable ? matchStyles.matchCardLocked : ''}`}
    >
      <div className={matchStyles.matchCardTopbar}>
        <div className={matchStyles.topbarLeft}>
          <span className={matchStyles.stagePill}>
            {t('customCompetition.questionPill', { number: question.questionNumber })}
          </span>
          {chip ? (
            <span className={`${matchStyles.statusChip} ${chip.className}`}>{chip.label}</span>
          ) : null}
        </div>
        <div className={styles.topbarRight}>
          {deadlineBlock(question.deadlineAt, locale)}
          {isOrganizer ? (
            <OrganizerActions question={question} groupId={groupId} poolId={poolId} />
          ) : null}
        </div>
      </div>

      <div className={matchStyles.matchCardBody}>
        <p className={styles.footballLeagueLabel}>
          {question.sourceCompetitionName ?? t('customCompetition.customMatchLabel')}
        </p>

        <div className={matchStyles.teams}>
          <div className={matchStyles.team}>
            <span className={matchStyles.teamName}>{question.homeTeamName}</span>
          </div>

          <div className={matchStyles.scoreInputs}>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={20}
              className={matchStyles.scoreBox}
              aria-label={t('predictions.homeScoreLabel', { team: question.homeTeamName ?? '' })}
              value={draft.home}
              disabled={!question.editable}
              onChange={(e) => setDraft((d) => ({ ...d, home: e.target.value }))}
              onBlur={() =>
                void commit(isLevel && question.canGoToPenalties ? draft.penaltyWinner : null)
              }
            />
            <span className={matchStyles.scoreSeparator}>×</span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={20}
              className={matchStyles.scoreBox}
              aria-label={t('predictions.awayScoreLabel', { team: question.awayTeamName ?? '' })}
              value={draft.away}
              disabled={!question.editable}
              onChange={(e) => setDraft((d) => ({ ...d, away: e.target.value }))}
              onBlur={() =>
                void commit(isLevel && question.canGoToPenalties ? draft.penaltyWinner : null)
              }
            />
          </div>

          <div className={`${matchStyles.team} ${matchStyles.teamAway}`}>
            <span className={matchStyles.teamName}>{question.awayTeamName}</span>
          </div>
        </div>

        {needsPenaltyPick && question.editable ? (
          <div className={matchStyles.penaltyPrompt}>
            <span className={matchStyles.penaltyPromptLabel}>{t('predictions.penaltyPrompt')}</span>
            <div className={matchStyles.penaltyButtons}>
              <button
                type="button"
                className={`${matchStyles.penaltyButton} ${draft.penaltyWinner === 'home' ? matchStyles.penaltyButtonActive : ''}`}
                onClick={() => {
                  setDraft((d) => ({ ...d, penaltyWinner: 'home' }));
                  void commit('home');
                }}
              >
                {t('predictions.penaltyWinnerLabel', { team: question.homeTeamName ?? '' })}
              </button>
              <button
                type="button"
                className={`${matchStyles.penaltyButton} ${draft.penaltyWinner === 'away' ? matchStyles.penaltyButtonActive : ''}`}
                onClick={() => {
                  setDraft((d) => ({ ...d, penaltyWinner: 'away' }));
                  void commit('away');
                }}
              >
                {t('predictions.penaltyWinnerLabel', { team: question.awayTeamName ?? '' })}
              </button>
            </div>
          </div>
        ) : null}

        {!question.editable && question.myPenaltyWinner ? (
          <p className={matchStyles.penaltyPickReadout}>
            {t('predictions.penaltyWinnerLabel', {
              team:
                question.myPenaltyWinner === 'home' ? question.homeTeamName : question.awayTeamName,
            })}
          </p>
        ) : null}

        {question.myHomeScore === null ? (
          <p className={styles.unanswered}>{t('customCompetition.unanswered')}</p>
        ) : null}

        {question.revealedAt && question.realHome !== null && question.realAway !== null ? (
          <div className={matchStyles.realScoreBanner}>
            <span className={matchStyles.realScoreLabel}>{t('predictions.finalScore')}</span>
            <span className={matchStyles.realScoreValue}>
              {question.realHome} × {question.realAway}
            </span>
          </div>
        ) : null}

        {saveState !== 'idle' ? (
          <p
            className={`${matchStyles.saveStatus} ${saveState === 'saved' ? matchStyles.saveStatusSaved : ''} ${saveState === 'error' ? matchStyles.saveStatusError : ''}`}
          >
            {saveState === 'saving' ? t('predictions.saving') : null}
            {saveState === 'saved' ? t('predictions.saved') : null}
            {saveState === 'error' ? t('predictions.saveFailed') : null}
          </p>
        ) : null}

        <PointsDetails question={question} />

        {canLock ? (
          <div className={matchStyles.lockRow}>
            <button
              type="button"
              className={matchStyles.lockButton}
              disabled={lock.isPending}
              onClick={() => {
                if (window.confirm(t('customCompetition.lockConfirm'))) lock.mutate();
              }}
            >
              {lock.isPending ? t('predictions.locking') : t('predictions.lockForever')}
            </button>
          </div>
        ) : null}

        <RevealSection
          question={question}
          render={(p) => {
            const line = `${p.homeScore} × ${p.awayScore}`;
            if (!p.penaltyWinner) return line;
            const winner =
              p.penaltyWinner === 'home' ? question.homeTeamName : question.awayTeamName;
            return `${line} (${winner ?? ''})`;
          }}
        />
      </div>

      {lockReasonLine(question, t)}
    </div>
  );
}

export default function CustomQuestionsSection({
  groupId,
  poolId,
  locale,
  data,
}: {
  groupId: string;
  poolId: string;
  locale: string;
  data: QuestionsForGroup;
}) {
  const { t } = useTranslation();

  return (
    <div className={matchStyles.matchList}>
      {!data.isParticipant ? (
        <div className="panel" style={{ padding: 'var(--space-4)' }}>
          <p>{t('predictions.notParticipant')}</p>
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            {t('predictions.notParticipantHint')}
          </p>
        </div>
      ) : null}

      {data.questions.map((question) =>
        question.kind === 'football' ? (
          <FootballQuestionCard
            key={question.id}
            question={question}
            groupId={groupId}
            poolId={poolId}
            locale={locale}
            isOrganizer={data.isOrganizer}
          />
        ) : question.kind === 'date' ? (
          <DateQuestionCard
            key={question.id}
            question={question}
            groupId={groupId}
            poolId={poolId}
            locale={locale}
            isOrganizer={data.isOrganizer}
          />
        ) : (
          <MultipleChoiceQuestionCard
            key={question.id}
            question={question}
            groupId={groupId}
            poolId={poolId}
            locale={locale}
            isOrganizer={data.isOrganizer}
          />
        ),
      )}

      {data.isOrganizer ? (
        <Link to={`/groups/${groupId}/bolaos/${poolId}/questions/new`} className={styles.addCard}>
          <span className={styles.addPlus}>+</span>
          {data.questions.length === 0 ? (
            <span className={styles.addHint}>{t('customCompetition.addFirstQuestionHint')}</span>
          ) : null}
        </Link>
      ) : data.questions.length === 0 ? (
        <p className="muted">{t('customCompetition.noQuestionsYet')}</p>
      ) : null}
    </div>
  );
}
